// @flow

import React, { type Node, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { createPortal } from 'react-dom';
import { type LookAndFeel } from '@tectonics/look-and-feel';
import { getInitialLookAndFeelCode, persistSelectedLookAndFeelCode } from './persistence';

type Props = {|
  availableLookAndFeels: ImmutableArray<LookAndFeel>,
  currentLookAndFeel: LookAndFeel,
  setCurrentLookAndFeel: (LookAndFeel) => void,
|};

export function LafChooser({ availableLookAndFeels, currentLookAndFeel, setCurrentLookAndFeel }: Props): Node {
  const initialLookAndFeel = getLookAndFeelByCode(availableLookAndFeels, getInitialLookAndFeelCode());
  const showing = useShowingLafChooser();

  useEffect(() => {
    if (initialLookAndFeel)
      setCurrentLookAndFeel(initialLookAndFeel);
  }, [initialLookAndFeel, setCurrentLookAndFeel]);

  const handleLookAndFeelChange = useCallback((e: SyntheticInputEvent<HTMLSelectElement>) => {
    const lookAndFeelCode = e.target.value;
    const lookAndFeel = getLookAndFeelByCode(availableLookAndFeels, lookAndFeelCode);
    persistSelectedLookAndFeelCode(lookAndFeelCode);
    setCurrentLookAndFeel(lookAndFeel || currentLookAndFeel);
  }, [availableLookAndFeels, setCurrentLookAndFeel, currentLookAndFeel]);

  const modalRoot = document.querySelector('#laf-chooser');

  if (!modalRoot) return null;
  if (!showing) return null;

  return createPortal(
    <>
      <GlobalStyle />

      <Root>

        <LookAndFeelLabel>
          Look and Feel Chooser:
        </LookAndFeelLabel>

        <LookAndFeelSelect value={currentLookAndFeel.code} onChange={handleLookAndFeelChange} autoFocus>
          {availableLookAndFeels.map(availableLookAndFeel => {
            return (
              <LookAndFeelOption key={availableLookAndFeel.code} value={availableLookAndFeel.code}>
                {availableLookAndFeel.displayName}
              </LookAndFeelOption>
            );
          })}
        </LookAndFeelSelect>

      </Root>
    </>,
    modalRoot,
  );
}

const GlobalStyle = createGlobalStyle`
  #laf-chooser {
    position: sticky;
    bottom: 0;
    z-index: 2;
  }
`;

const Root = styled.div`
  background: #fff;
  padding: 32px;
  box-shadow: 0 -4px 4px rgba(0, 0, 0, 0.05);
`;

const LookAndFeelLabel = styled.label`
  margin-right: 16px;
`;

const LookAndFeelSelect = styled.select``;

const LookAndFeelOption = styled.option``;

function useShowingLafChooser(initial: boolean = false): boolean {
  const [showing, setShowing] = useState<boolean>(initial);

  useLayoutEffect(() => {
    function keyListener(event: KeyboardEvent) {
      if (event.which === 75 && (navigator.platform.match(/Mac/) ? event.metaKey : event.ctrlKey)) {
        event.preventDefault();
        setShowing(visible => !visible);
      } else if (event.which === 27) {
        setShowing(false);
      }
    }

    document.addEventListener('keydown', keyListener);
    return () => document.removeEventListener('keydown', keyListener);
  });

  return showing;
}

function getLookAndFeelByCode(lookAndFeels: ImmutableArray<LookAndFeel>, code: ?string): ?LookAndFeel {
  return lookAndFeels.find(it => it.code === code);
}
